import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { environment } from '../../../environments/environment'
import { map, Observable } from 'rxjs'
import {
  GenerationUnit,
  SOLAR_ORIENTATION_MAPPING,
  SolarGenerationUnit,
  TRACKED_MODULE,
  WindGenerationUnit,
} from '../models/market-register-data'
import {
  MarketRegisterResource,
  MarketRegisterSolarResource,
  MarketRegisterWindResource,
} from '../models/technical-resource'
import moment from 'moment/moment'
import { ProductionTechnology } from '../models/production-technology.enum'
import { SPINNER_MESSAGE } from '../interceptors/loading-spinner.interceptor'

export const GENERATION_UNIT_DATE_FORMATTER = 'YYYY-MM-DD'

@Injectable({
  providedIn: 'root',
})
export class MarketRegisterDataService {
  readonly httpClient = inject(HttpClient)

  private readonly baseUrl = environment.apiUrl
  private readonly context = {
    context: new HttpContext().set(SPINNER_MESSAGE, 'Daten werden aus dem Marktstammdatenregister abgerufen'),
  }

  getGenerationUnitData(
    technology: ProductionTechnology,
    marketRegisterNumber: string
  ): Observable<MarketRegisterSolarResource | MarketRegisterWindResource> {
    switch (technology) {
      case ProductionTechnology.Solar:
        return this.getSolarGenerationUnitData(marketRegisterNumber)
      case ProductionTechnology.WindOnshore:
        return this.getWindGenerationUnitData(marketRegisterNumber)
      default:
        throw new Error(`Unsupported technology: ${technology}`)
    }
  }

  private getSolarGenerationUnitData(marketRegisterNumber: string): Observable<MarketRegisterSolarResource> {
    return this.httpClient
      .get<MarketRegisterResponse>(`${this.baseUrl}/validate/mastr/Solar/${marketRegisterNumber}`, this.context)
      .pipe(
        map((response: MarketRegisterResponse) => response.einheit as SolarGenerationUnit),
        map((data) => {
          const genericResource = this.convertToGenericResource(data)
          return this.convertToSolarResource(data, genericResource)
        })
      )
  }

  private getWindGenerationUnitData(marketRegisterNumber: string): Observable<MarketRegisterWindResource> {
    return this.httpClient
      .get<MarketRegisterResponse>(`${this.baseUrl}/validate/mastr/Wind/${marketRegisterNumber}`, this.context)
      .pipe(
        map((response: MarketRegisterResponse) => response.einheit as WindGenerationUnit),
        map((data) => {
          const genericResource = this.convertToGenericResource(data)
          return this.convertToWindResource(data, genericResource)
        })
      )
  }

  private convertToGenericResource(data: GenerationUnit) {
    const resource: MarketRegisterResource = {
      installedCapacityKW: data.bruttoleistung,
      commissioningDate: this.formatDate(data.inbetriebnahmedatum),
      commissioningDatePlanned: this.formatDate(data.geplantesInbetriebnahmedatum),
      longitude: data.laengengrad,
      latitude: data.breitengrad,
    }
    resource.commissioningDateCommon = resource.commissioningDate || resource.commissioningDatePlanned
    return resource
  }

  private formatDate(value?: string | null): moment.Moment | null {
    return value ? moment(value, GENERATION_UNIT_DATE_FORMATTER) : null
  }

  private convertToWindResource(data: WindGenerationUnit, windResource: MarketRegisterWindResource) {
    windResource.turbineHubHeight = data.nabenhoehe
    return windResource
  }

  private convertToSolarResource(data: SolarGenerationUnit, solarResource: MarketRegisterSolarResource) {
    if (data.hauptausrichtung === TRACKED_MODULE) {
      solarResource.solarTrackedModules = true
      solarResource.solarOrientation = null
    } else if (data.hauptausrichtung) {
      solarResource.solarTrackedModules = false
      solarResource.solarOrientation = SOLAR_ORIENTATION_MAPPING[data.hauptausrichtung]
    }
    return solarResource
  }
}

interface MarketRegisterResponse {
  einheit: SolarGenerationUnit | WindGenerationUnit
}
